import * as React from 'react'
import styled from 'styled-components'
import theme from 'theme'
import useTranslations from 'hooks/useTranslations'
import { Props } from './types'
import { useEffect, useRef, useState } from 'react'
import { ReactComponent as Swipe } from 'assets/swipe.svg'

export default function InternationalSizes(props: Props) {
  if (!props.sizeChartData) return null

  const validSizeChartKeys = [
    'SIZE_CHART_MEN_TOP',
    'SIZE_CHART_MEN_BOTTOM',
    'SIZE_CHART_WOMEN'
  ]

  if (!validSizeChartKeys.includes(props.sizeChartData.sizeChartKey))
    return null

  const t = useTranslations<'templates-PDP'>()
  const tableHeaderCount =
    props.sizeChartData.sizeChartKey === 'SIZE_CHART_MEN_TOP' ? 6 : 5

  const [tableIsOverflowing, setTableIsOverflowing] = useState(false)
  const tableRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const checkOverflow = () => {
      if (tableRef.current) {
        const hasOverflow =
          tableRef.current.scrollHeight > tableRef.current.clientHeight ||
          tableRef.current.scrollWidth > tableRef.current.clientWidth
        setTableIsOverflowing(hasOverflow)
      }
    }

    checkOverflow() // Check overflow on mount

    // Re-check on window resize (if overflow might change dynamically)
    window.addEventListener('resize', checkOverflow)
    return () => window.removeEventListener('resize', checkOverflow)
  }, [])

  return (
    <Wrapper
      className="InternationalSizes"
      data-cy-collection="InternationalSizes"
      data-cy-state={props['data-cy-state']}
      $tableHeaderCount={tableHeaderCount}
    >
      {/* eslint-disable @kaminrunde/firescout/jsx-expression-needs-state */}
      <div className="international_size_table_title_wrapper">
        <h3 className="international_size_table_title">
          {t('sizeChart_international_size_table_title')}
        </h3>
        {tableIsOverflowing && (
          <div className="swipe_icon_wrapper" data-cy-state="show-swipe-icon">
            <Swipe></Swipe>
            <p>{t('sizeChart_scrollable_label')}</p>
          </div>
        )}
      </div>
      <div className="international_sizes_table" ref={tableRef}>
        {props.sizeChartData.sizeChartKey === 'SIZE_CHART_MEN_TOP' && (
          <table>
            <tr>
              <th>INT</th>
              <th>D</th>
              <th>EU (European Collar)</th>
              <th>FR</th>
              <th>IT</th>
              <th>ES</th>
            </tr>
            <tr>
              <td>S</td>
              <td>44</td>
              <td>37/38</td>
              <td>44</td>
              <td>44</td>
              <td>44</td>
            </tr>
            <tr>
              <td>S</td>
              <td>46</td>
              <td>37/38</td>
              <td>46</td>
              <td>46</td>
              <td>46</td>
            </tr>
            <tr>
              <td>M</td>
              <td>48</td>
              <td>39/40</td>
              <td>48</td>
              <td>48</td>
              <td>48</td>
            </tr>
            <tr>
              <td>M</td>
              <td>50</td>
              <td>39/40</td>
              <td>50</td>
              <td>50</td>
              <td>50</td>
            </tr>
            <tr>
              <td>L</td>
              <td>52</td>
              <td>41/42</td>
              <td>52</td>
              <td>52</td>
              <td>52</td>
            </tr>
            <tr>
              <td>L</td>
              <td>54</td>
              <td>41/42</td>
              <td>54</td>
              <td>54</td>
              <td>54</td>
            </tr>
            <tr>
              <td>XL</td>
              <td>56</td>
              <td>43/44</td>
              <td>56</td>
              <td>56</td>
              <td>56</td>
            </tr>
            <tr>
              <td>XL</td>
              <td>58</td>
              <td>43/44</td>
              <td>58</td>
              <td>58</td>
              <td>58</td>
            </tr>
            <tr>
              <td>2XL</td>
              <td>60</td>
              <td>45/46</td>
              <td>60</td>
              <td>60</td>
              <td>60</td>
            </tr>
            <tr>
              <td>2XL</td>
              <td>62</td>
              <td>45/46</td>
              <td>62</td>
              <td>62</td>
              <td>62</td>
            </tr>
            <tr>
              <td>3XL</td>
              <td>64</td>
              <td>47/48</td>
              <td>64</td>
              <td>64</td>
              <td>64</td>
            </tr>
            <tr>
              <td>3XL</td>
              <td>66</td>
              <td>47/48</td>
              <td>66</td>
              <td>66</td>
              <td>66</td>
            </tr>
          </table>
        )}
        {props.sizeChartData.sizeChartKey === 'SIZE_CHART_MEN_BOTTOM' && (
          <table>
            <tr>
              <th>INT</th>
              <th>D</th>
              <th>FR</th>
              <th>IT</th>
              <th>ES</th>
            </tr>
            <tr>
              <td>S</td>
              <td>44</td>
              <td>38</td>
              <td>44</td>
              <td>38</td>
            </tr>
            <tr>
              <td>S</td>
              <td>46</td>
              <td>40</td>
              <td>46</td>
              <td>40</td>
            </tr>
            <tr>
              <td>M</td>
              <td>48</td>
              <td>42</td>
              <td>48</td>
              <td>42</td>
            </tr>
            <tr>
              <td>M</td>
              <td>50</td>
              <td>44</td>
              <td>50</td>
              <td>44</td>
            </tr>
            <tr>
              <td>L</td>
              <td>52</td>
              <td>46</td>
              <td>52</td>
              <td>46</td>
            </tr>
            <tr>
              <td>L</td>
              <td>54</td>
              <td>48</td>
              <td>54</td>
              <td>48</td>
            </tr>
            <tr>
              <td>XL</td>
              <td>56</td>
              <td>50</td>
              <td>56</td>
              <td>50</td>
            </tr>
            <tr>
              <td>XL</td>
              <td>58</td>
              <td>52</td>
              <td>58</td>
              <td>52</td>
            </tr>
            <tr>
              <td>2XL</td>
              <td>60</td>
              <td>54</td>
              <td>60</td>
              <td>54</td>
            </tr>
            <tr>
              <td>2XL</td>
              <td>62</td>
              <td>56</td>
              <td>62</td>
              <td>56</td>
            </tr>
            <tr>
              <td>3XL</td>
              <td>64</td>
              <td>58</td>
              <td>64</td>
              <td>58</td>
            </tr>
            <tr>
              <td>3XL</td>
              <td>66</td>
              <td>60</td>
              <td>66</td>
              <td>60</td>
            </tr>
          </table>
        )}
        {props.sizeChartData.sizeChartKey === 'SIZE_CHART_WOMEN' && (
          <table>
            <tr>
              <th>INT</th> <th>D</th> <th>FR</th> <th>IT</th> <th>ES</th>
            </tr>
            <tr>
              <td>XS</td> <td>34</td> <td>36</td> <td>40</td> <td>36</td>
            </tr>
            <tr>
              <td>S</td> <td>36</td> <td>38</td> <td>42</td> <td>38</td>
            </tr>
            <tr>
              <td>S</td> <td>38</td> <td>40</td> <td>44</td> <td>40</td>
            </tr>
            <tr>
              <td>M</td> <td>40</td> <td>42</td> <td>46</td> <td>42</td>
            </tr>
            <tr>
              <td>M</td> <td>42</td> <td>44</td> <td>48</td> <td>44</td>
            </tr>
            <tr>
              <td>L</td> <td>44</td> <td>46</td> <td>50</td> <td>46</td>
            </tr>
            <tr>
              <td>L</td> <td>46</td> <td>48</td> <td>52</td> <td>48</td>
            </tr>
            <tr>
              <td>XL</td> <td>48</td> <td>50</td> <td>54</td> <td>50</td>
            </tr>
            <tr>
              <td>XL</td> <td>50</td> <td>52</td> <td>56</td> <td>52</td>
            </tr>
            <tr>
              <td>2XL</td> <td>52</td> <td>54</td> <td>58</td> <td>54</td>
            </tr>
            <tr>
              <td>2XL</td> <td>54</td> <td>56</td> <td>60</td> <td>56</td>
            </tr>
            <tr>
              <td>3XL</td> <td>56</td> <td>58</td> <td>62</td> <td>58</td>
            </tr>
          </table>
        )}
      </div>
    </Wrapper>
  )
}

const Wrapper = styled.div<{ $tableHeaderCount: number }>`
  border-radius: ${theme.spacing('xs')};
  background-color: ${theme.colors.white};

  > .international_size_table_title_wrapper {
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    > .international_size_table_title {
      margin-bottom: ${theme.spacing('m')};
    }

    > .swipe_icon_wrapper {
      display: flex;
      flex-direction: row;
      align-items: center;
      margin-bottom: 1.25rem;

      > p {
        margin: 0;
        margin-left: 0.5rem;
      }
    }
  }

  > .international_sizes_table {
    width: 100%;
    overflow-x: auto;

    > table {
      width: 100%;
      min-width: 700px;
      border-collapse: collapse;
      table-layout: fixed;

      th {
        text-align: center;
        width: 25%;
        height: 3.125rem;
        table-layout: fixed;
      }

      tr {
        text-align: center;
        height: 3.125rem;
        table-layout: fixed;

        td {
          // There are currently 5 columns in the table
          width: calc(100% / ${(p) => p.$tableHeaderCount});

          text-align: center;
          height: 3.125rem;
          table-layout: fixed;
        }
      }

      tr:nth-child(odd) {
        background-color: ${theme.colors.shade.b6};
      }

      tr:nth-child(even) {
        background-color: ${theme.colors.white};
      }
    }
  }
`
